/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from "prop-types";
import { htmlIdGenerator } from '../../services/accessibility';
import classnames from 'classnames';
import { keyCodes } from '../../services';
import { EuiDataGridColumnResizer } from './data_grid_column_resizer';
import { EuiScreenReaderOnly } from '../accessibility';
import tabbable from 'tabbable';
export var EuiDataGridHeaderCell = props => {
  const {
    column,
    index,
    columnWidths,
    schema,
    defaultColumnWidth,
    setColumnWidth,
    sorting,
    focusedCell,
    setFocusedCell,
    headerIsInteractive,
    className
  } = props;
  const {
    id,
    display
  } = column;
  const width = columnWidths[id] || defaultColumnWidth;
  const ariaProps = {};
  let screenReaderId;
  let sortString;

  if (sorting) {
    const sortedColumnIds = new Set(sorting.columns.map(({
      id
    }) => id));

    if (sorting.columns.length === 1 && sortedColumnIds.has(id)) {
      const sortDirection = sorting.columns[0].direction;
      let sortValue = 'other';

      if (sortDirection === 'asc') {
        sortValue = 'ascending';
      } else if (sortDirection === 'desc') {
        sortValue = 'descending';
      }

      ariaProps['aria-sort'] = sortValue;
    } else if (sorting.columns.length >= 2 && sortedColumnIds.has(id)) {
      sortString = sorting.columns.map(col => `Sorted by ${col.id} ${col.direction}`).join(' then ');
      screenReaderId = htmlIdGenerator()();
      ariaProps['aria-describedby'] = screenReaderId;
    }
  }

  const columnType = schema[id] ? schema[id].columnType : null;
  const classes = classnames('euiDataGridHeaderCell', {
    [`euiDataGridHeaderCell--${columnType}`]: columnType
  }, className);
  const headerRef = useRef(null);
  const isFocused = focusedCell != null && focusedCell[0] === index && focusedCell[1] === -1;
  const [isCellEntered, setIsCellEntered] = useState(false);
  const enableInteractives = useCallback(() => {
    if (headerRef.current) {
      const interactiveElements = headerRef.current.querySelectorAll('[data-euigrid-tab-managed]');

      for (let i = 0; i < interactiveElements.length; i++) {
        interactiveElements[i].setAttribute('tabIndex', '0');
      }
    }
  }, []);
  const disableInteractives = useCallback(() => {
    if (headerRef.current) {
      const tababbles = tabbable(headerRef.current);

      if (tababbles.length > 1) {
        console.warn(`EuiDataGridHeaderCell expects at most 1 tabbable element, ${tababbles.length} found instead`);
      }

      for (let i = 0; i < tababbles.length; i++) {
        const element = tababbles[i];
        element.setAttribute('data-euigrid-tab-managed', 'true');
        element.setAttribute('tabIndex', '-1');
      }
    }
  }, []);
  useEffect(() => {
    if (headerRef.current) {
      if (isCellEntered) {
        enableInteractives();
        const tabbables = tabbable(headerRef.current);

        if (tabbables.length > 0) {
          tabbables[0].focus();
        }
      } else {
        disableInteractives();
      }
    }
  }, [disableInteractives, enableInteractives, isCellEntered]);
  useEffect(() => {
    if (headerRef.current) {
      if (isFocused) {
        const interactives = headerRef.current.querySelectorAll('[data-euigrid-tab-managed]');

        if (interactives.length === 1) {
          setIsCellEntered(true);
        } else {
          headerRef.current.focus();
        }
      } else {
        setIsCellEntered(false);
      } // focusin bubbles while focus does not, and this needs to react to children gaining focus


      function onFocusIn(e) {
        if (headerIsInteractive === false) {
          // header is not interactive, avoid focusing
          requestAnimationFrame(() => headerRef.current.blur());
          e.preventDefault();
          return false;
        } else {
          // take the focus
          if (focusedCell == null || focusedCell[0] !== index || focusedCell[1] !== -1) {
            setFocusedCell([index, -1]);
          } else if (headerRef.current) {
            // this cell already had the grid's focus, so re-enable interactives
            enableInteractives();
            setIsCellEntered(true); // if there is only one interactive element shift focus to the interactive element

            const tabbables = tabbable(headerRef.current);

            if (tabbables.length === 1) {
              tabbables[0].focus();
            }
          }
        }
      } // focusout bubbles while blur does not, and this needs to react to the children losing focus


      function onFocusOut() {
        // wait for the next element to receive focus, then update interactives' state
        requestAnimationFrame(() => {
          if (headerRef.current) {
            if (headerRef.current.contains(document.activeElement) === false) {
              setIsCellEntered(false);
            }
          }
        });
      }

      function onKeyUp(e) {
        switch (e.keyCode) {
          case keyCodes.ENTER:
            {
              e.preventDefault();
              setIsCellEntered(true);
              break;
            }

          case keyCodes.ESCAPE:
            {
              e.preventDefault(); // move focus to cell

              setIsCellEntered(false);
              headerRef.current.focus();
              break;
            }

          case keyCodes.F2:
            {
              e.preventDefault();

              if (document.activeElement === headerRef.current) {
                // move focus into cell's interactives
                setIsCellEntered(true);
              } else {
                // move focus to cell
                setIsCellEntered(false);
                headerRef.current.focus();
              }

              break;
            }
        }
      }

      const headerNode = headerRef.current; // @ts-ignore-next line TS doesn't have focusin

      headerNode.addEventListener('focusin', onFocusIn);
      headerNode.addEventListener('focusout', onFocusOut);
      headerNode.addEventListener('keyup', onKeyUp);
      return () => {
        // @ts-ignore-next line TS doesn't have focusin
        headerNode.removeEventListener('focusin', onFocusIn);
        headerNode.removeEventListener('focusout', onFocusOut);
        headerNode.removeEventListener('keyup', onKeyUp);
      };
    }
  }, [enableInteractives, headerIsInteractive, isFocused, setIsCellEntered, focusedCell, setFocusedCell, index]);
  return <div role="columnheader" {...ariaProps} ref={headerRef} tabIndex={isFocused && !isCellEntered ? 0 : -1} className={classes} data-test-subj={`dataGridHeaderCell-${id}`} style={width != null ? {
    width: `${width}px`
  } : {}}>
      {column.isResizable !== false && width != null ? <EuiDataGridColumnResizer columnId={id} columnWidth={width} setColumnWidth={setColumnWidth} /> : null}

      <div className="euiDataGridHeaderCell__content">{display || id}</div>
      {sorting && sorting.columns.length >= 2 && <EuiScreenReaderOnly>
          <div id={screenReaderId}>{sortString}</div>
        </EuiScreenReaderOnly>}
    </div>;
};
EuiDataGridHeaderCell.propTypes = {
  column: PropTypes.shape({
    /**
       * The unique identifier for this column
       */
    id: PropTypes.string.isRequired,

    /**
       * A `ReactNode` used when rendering the column header. When providing complicated content, please make sure to utilize CSS to respect truncation as space allows. Check the docs example.
       */
    display: PropTypes.node,

    /**
       * A Schema to use for the column. Built-in values are ['boolean', 'currency', 'datetime', 'numeric', 'json'] but can be expanded by defining your own #EuiDataGrid `schemaDetectors` (for in-memory detection). In general, it is advised to pass in a value here when you are sure of the schema ahead of time, so that you don't need to rely on the automatic detection.
       */
    schema: PropTypes.string,

    /**
       * Defauls to true. Defines shether or not the column's cells can be expanded with a popup onClick / keydown.
       */
    isExpandable: PropTypes.bool,

    /**
       * Whether this column's width can be changed by the user, defaults to true
       */
    isResizable: PropTypes.bool,

    /**
       * Initial width (in pixels) of the column
       */
    initialWidth: PropTypes.number,

    /**
       * Whether this column is sortable
       */
    isSortable: PropTypes.bool,

    /**
       * Default sort direction of the column
       */
    defaultSortDirection: PropTypes.oneOf(["asc", "desc"])
  }).isRequired,
  index: PropTypes.number.isRequired,
  className: PropTypes.string
};

try {
  // @ts-ignore
  EuiDataGridHeaderCell.displayName = "EuiDataGridHeaderCell"; // @ts-ignore

  EuiDataGridHeaderCell.__docgenInfo = {
    "description": "",
    "displayName": "EuiDataGridHeaderCell",
    "props": {
      "column": {
        "defaultValue": null,
        "description": "",
        "name": "column",
        "required": true,
        "type": {
          "name": "EuiDataGridColumn"
        }
      },
      "index": {
        "defaultValue": null,
        "description": "",
        "name": "index",
        "required": true,
        "type": {
          "name": "number"
        }
      },
      "className": {
        "defaultValue": null,
        "description": "",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "schema": {
        "defaultValue": null,
        "description": "",
        "name": "schema",
        "required": true,
        "type": {
          "name": "EuiDataGridSchema"
        }
      },
      "sorting": {
        "defaultValue": null,
        "description": "",
        "name": "sorting",
        "required": false,
        "type": {
          "name": "EuiDataGridSorting | undefined"
        }
      },
      "trailingControlColumns": {
        "defaultValue": null,
        "description": "",
        "name": "trailingControlColumns",
        "required": false,
        "type": {
          "name": "EuiDataGridControlColumn[] | undefined"
        }
      },
      "focusedCell": {
        "defaultValue": null,
        "description": "",
        "name": "focusedCell",
        "required": false,
        "type": {
          "name": "EuiDataGridFocusedCell | undefined"
        }
      },
      "setFocusedCell": {
        "defaultValue": null,
        "description": "",
        "name": "setFocusedCell",
        "required": true,
        "type": {
          "name": "Function"
        }
      },
      "headerIsInteractive": {
        "defaultValue": null,
        "description": "",
        "name": "headerIsInteractive",
        "required": true,
        "type": {
          "name": "boolean"
        }
      },
      "columnWidths": {
        "defaultValue": null,
        "description": "",
        "name": "columnWidths",
        "required": true,
        "type": {
          "name": "EuiDataGridColumnWidths"
        }
      },
      "defaultColumnWidth": {
        "defaultValue": null,
        "description": "",
        "name": "defaultColumnWidth",
        "required": false,
        "type": {
          "name": "number | null | undefined"
        }
      },
      "setColumnWidth": {
        "defaultValue": null,
        "description": "",
        "name": "setColumnWidth",
        "required": true,
        "type": {
          "name": "(columnId: string, width: number) => void"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/datagrid/data_grid_header_cell.tsx#EuiDataGridHeaderCell"] = {
      docgenInfo: EuiDataGridHeaderCell.__docgenInfo,
      name: "EuiDataGridHeaderCell",
      path: "src/components/datagrid/data_grid_header_cell.tsx#EuiDataGridHeaderCell"
    };
} catch (__react_docgen_typescript_loader_error) {}